export type Feld =
  | "datum"
  | "betrag"
  | "soll"
  | "haben"
  | "text"
  | "partner"
  | "beleg"
  | "konto_iban"
  | "status";
export type Zuordnung = Partial<Record<Feld, number>>;
export type Rohzeile = (string | number | Date | null | undefined)[];

export const FELDER: { feld: Feld; name: string }[] = [
  { feld: "datum", name: "Datum" },
  { feld: "betrag", name: "Betrag" },
  { feld: "soll", name: "Soll" },
  { feld: "haben", name: "Haben" },
  { feld: "text", name: "Text" },
  { feld: "partner", name: "Partner" },
  { feld: "beleg", name: "Beleg" },
  { feld: "konto_iban", name: "Eigene IBAN" },
  { feld: "status", name: "Status" },
];

const SPALTEN: Record<Feld, string[]> = {
  datum: [
    "buchungsdatum",
    "datum",
    "date",
    "booking date",
    "transaction date",
    "ausführungsdatum",
    "execution date",
    "completed at",
    "wertstellung",
    "value date",
  ],
  betrag: [
    "betrag",
    "betrag (eur)",
    "betrag in eur",
    "amount",
    "amount (eur)",
    "transaction amount",
    "umsatz",
  ],
  soll: ["soll", "debit", "ausgang"],
  haben: ["haben", "credit", "eingang"],
  text: [
    "verwendungszweck",
    "buchungstext",
    "text",
    "purpose",
    "payment reference",
    "reference",
    "referenz",
    "beschreibung",
    "description",
    "details",
    "note",
  ],
  partner: [
    "partner",
    "gegenpartei",
    "empfänger",
    "zahlungsempfänger",
    "auftraggeber",
    "counterparty",
    "counterparty name",
    "beneficiary",
    "recipient",
    "sender",
    "name",
  ],
  beleg: [
    "belegnr",
    "belegnummer",
    "beleg",
    "receipt",
    "document number",
    "rechnungsnummer",
    "invoice number",
  ],
  konto_iban: [
    "konto-iban",
    "kontoiban",
    "eigene iban",
    "account iban",
    "my iban",
  ],
  status: ["status", "state"],
};
const NICHT_AUSGEFUEHRT = [
  "pending",
  "declined",
  "rejected",
  "failed",
  "cancel",
  "abgelehnt",
  "storniert",
  "ausstehend",
  "fehlgeschlagen",
  "vorgemerkt",
];
const IBAN_MUSTER = /DE\d{2}(?: ?\d){18}/;

const zelle = (w: Rohzeile[number]) =>
  w == null ? "" : w instanceof Date ? w.toISOString().slice(0, 10) : String(w);

export const csvZerlegen = (inhalt: string, trenner: string): string[][] => {
  const zeilen: string[][] = [];
  let zeile: string[] = [];
  let feld = "";
  let inQuote = false;
  for (let i = 0; i < inhalt.length; i++) {
    const c = inhalt[i];
    if (inQuote) {
      if (c === '"' && inhalt[i + 1] === '"') {
        feld += '"';
        i++;
      } else if (c === '"') inQuote = false;
      else feld += c;
    } else if (c === '"') inQuote = true;
    else if (c === trenner) {
      zeile.push(feld);
      feld = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && inhalt[i + 1] === "\n") i++;
      zeile.push(feld);
      zeilen.push(zeile);
      zeile = [];
      feld = "";
    } else feld += c;
  }
  if (feld || zeile.length) {
    zeile.push(feld);
    zeilen.push(zeile);
  }
  return zeilen;
};

export const csvLesen = (roh: ArrayBuffer): string[][] => {
  let inhalt: string;
  try {
    inhalt = new TextDecoder("utf-8", { fatal: true }).decode(roh);
  } catch {
    inhalt = new TextDecoder("windows-1252").decode(roh);
  }
  const kandidaten = [";", ",", "\t"].map((t) =>
    csvZerlegen(inhalt.replace(/^\uFEFF/, ""), t),
  );
  const breite = (z: string[][]) => Math.max(0, ...z.map((r) => r.length));
  return kandidaten.reduce((a, b) => (breite(b) > breite(a) ? b : a));
};

export const xlsxLesen = async (roh: ArrayBuffer): Promise<Rohzeile[]> => {
  const { default: ExcelJS } = await import("exceljs");
  const mappe = new ExcelJS.Workbook();
  await mappe.xlsx.load(roh);
  const zeilen: Rohzeile[] = [];
  mappe.worksheets[0].eachRow({ includeEmpty: true }, (reihe) => {
    const werte = (reihe.values as unknown[]).slice(1).map((v) => {
      if (v && typeof v === "object" && !(v instanceof Date)) {
        const o = v as { result?: unknown; text?: unknown };
        return (o.result ?? o.text ?? "") as Rohzeile[number];
      }
      return v as Rohzeile[number];
    });
    zeilen.push(werte);
  });
  return zeilen;
};

export const kopfFinden = (
  zeilen: Rohzeile[],
): { kopf: number; zuordnung: Zuordnung } | null => {
  for (let nr = 0; nr < Math.min(30, zeilen.length); nr++) {
    const namen = zeilen[nr].map((z) => zelle(z).trim().toLowerCase());
    const zuordnung: Zuordnung = {};
    for (const [feld, synonyme] of Object.entries(SPALTEN) as [
      Feld,
      string[],
    ][]) {
      const wort = synonyme.find((s) => namen.includes(s));
      if (wort) zuordnung[feld] = namen.indexOf(wort);
    }
    if (zuordnungGueltig(zuordnung)) return { kopf: nr, zuordnung };
  }
  return null;
};

export const zuordnungGueltig = (z: Zuordnung) =>
  z.datum !== undefined &&
  (z.betrag !== undefined || z.soll !== undefined || z.haben !== undefined);

// Kennung einer Kopfzeile, unter der die Zuordnung gemerkt wird
export const kopfSignatur = (zeile: Rohzeile) =>
  zeile.map((z) => zelle(z).trim().toLowerCase()).join("|");

export const kopfRaten = (zeilen: Rohzeile[]) => {
  const i = zeilen
    .slice(0, 30)
    .findIndex((z) => z.filter((w) => zelle(w).trim()).length >= 3);
  return Math.max(0, i);
};

export const betragLesen = (wert: Rohzeile[number]): number | null => {
  if (typeof wert === "number") return Math.round(wert * 100);
  let t = zelle(wert)
    .replace(/[^\d,.\-−]/g, "")
    .replace("−", "-");
  if (!t || t === "-") return null;
  const trenner = Math.max(t.lastIndexOf(","), t.lastIndexOf("."));
  if (trenner >= 0 && t.length - trenner - 1 !== 3) {
    t = `${t.slice(0, trenner).replace(/[,.]/g, "")}.${t.slice(trenner + 1)}`;
  } else {
    t = t.replace(/[,.]/g, "");
  }
  const zahl = Number(t);
  return Number.isNaN(zahl) ? null : Math.round(zahl * 100);
};

export const datumLesen = (wert: Rohzeile[number]): string | null => {
  if (wert instanceof Date) return wert.toISOString().slice(0, 10);
  const t = zelle(wert).trim().split(/[ T]/)[0];
  let m = t.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4}|\d{2})$/);
  if (m) {
    const jahr = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${jahr}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  }
  m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? t : null;
};

export type GeleseneZeile = {
  datum: string;
  betrag: number; // Cent
  partner: string;
  text: string;
  beleg: string;
  iban: string | null;
  schluessel: string;
};

export const ibanFinden = (
  dateiname: string,
  zeilen: Rohzeile[],
  kopf: number,
) => {
  const treffer = dateiname.match(IBAN_MUSTER);
  if (treffer) return treffer[0].replace(/ /g, "");
  for (const zeile of zeilen.slice(0, kopf)) {
    const t = zeile.map(zelle).join(" ").match(IBAN_MUSTER);
    if (t) return t[0].replace(/ /g, "");
  }
  return null;
};

const hash = async (t: string) => {
  const bytes = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(t),
  );
  return [...new Uint8Array(bytes)]
    .slice(0, 16)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

// Der Schlüssel zählt gleiche Zeilen mit: zwei echte gleiche Zeilen bleiben beide,
// ein zweiter Import derselben Datei ergibt dieselben Schlüssel und legt nichts an.
export const zeilenLesen = async (
  zeilen: Rohzeile[],
  kopf: number,
  z: Zuordnung,
) => {
  const feld = (zeile: Rohzeile, f: Feld) => {
    const i = z[f];
    return i === undefined ? "" : (zeile[i] ?? "");
  };
  const gelesen: Omit<GeleseneZeile, "schluessel">[] = [];
  let uebersprungen = 0;
  for (const zeile of zeilen.slice(kopf + 1)) {
    const status = zelle(feld(zeile, "status")).toLowerCase();
    if (NICHT_AUSGEFUEHRT.some((n) => status.startsWith(n))) {
      uebersprungen++;
      continue;
    }
    const datum = datumLesen(feld(zeile, "datum"));
    const betrag =
      z.betrag !== undefined
        ? betragLesen(feld(zeile, "betrag"))
        : (betragLesen(feld(zeile, "haben")) ?? 0) -
          Math.abs(betragLesen(feld(zeile, "soll")) ?? 0);
    if (!datum || betrag === null) {
      if (zeile.some((w) => zelle(w).trim())) uebersprungen++;
      continue;
    }
    const partner = zelle(feld(zeile, "partner")).trim();
    const text = zelle(feld(zeile, "text"))
      .split(/\s+/)
      .filter(Boolean)
      .join(" ");
    const iban =
      zelle(feld(zeile, "konto_iban"))
        .match(IBAN_MUSTER)?.[0]
        .replace(/ /g, "") ?? null;
    gelesen.push({
      datum,
      betrag,
      partner,
      text: text || partner,
      beleg: zelle(feld(zeile, "beleg")).trim(),
      iban,
    });
  }
  const gesehen = new Map<string, number>();
  const ergebnis: GeleseneZeile[] = [];
  for (const b of gelesen) {
    const roh = `${b.datum}|${b.betrag}|${b.text.toLowerCase()}`;
    const n = (gesehen.get(roh) ?? 0) + 1;
    gesehen.set(roh, n);
    ergebnis.push({ ...b, schluessel: await hash(`${roh}|${n}`) });
  }
  return { zeilen: ergebnis, uebersprungen };
};
